/**
 * Foundation · Drafts — hook generico per bozze con salvataggio automatico e blocco ottimistico.
 * Indipendente dal workflow: riceve `save(payload, version)` → { version, updated_at }.
 */
import { useCallback, useEffect, useRef, useState } from "react";

export const DRAFT_AUTOSAVE_MS = 2000;

export function useFoundationDraft({ save, enabled = true, delay = DRAFT_AUTOSAVE_MS }) {
  const [state, setState] = useState({ status: "idle", version: null, savedAt: null, error: null });
  const pending = useRef(null);
  const timer = useRef(null);
  const versionRef = useRef(null);
  const saving = useRef(false);

  const reset = useCallback((version, savedAt = null) => {
    versionRef.current = version ?? null;
    pending.current = null;
    clearTimeout(timer.current);
    setState({ status: version ? "saved" : "idle", version: version ?? null, savedAt, error: null });
  }, []);

  const flush = useCallback(async () => {
    clearTimeout(timer.current);
    if (!pending.current || saving.current || !enabled) return state.status !== "conflict";
    const payload = pending.current;
    pending.current = null;
    saving.current = true;
    setState((s) => ({ ...s, status: "saving" }));
    try {
      const res = await save(payload, versionRef.current);
      versionRef.current = res.version;
      setState({ status: pending.current ? "dirty" : "saved", version: res.version, savedAt: res.updated_at, error: null });
      return true;
    } catch (e) {
      const conflict = e?.response?.status === 409;
      if (!conflict) pending.current = pending.current || payload;
      setState((s) => ({ ...s, status: conflict ? "conflict" : "error", error: e }));
      return false;
    } finally {
      saving.current = false;
      if (pending.current) timer.current = setTimeout(() => flush(), delay);
    }
  }, [save, enabled, delay, state.status]);

  const schedule = useCallback((payload) => {
    if (!enabled) return;
    pending.current = payload;
    setState((s) => (s.status === "conflict" ? s : { ...s, status: "dirty" }));
    clearTimeout(timer.current);
    timer.current = setTimeout(() => flush(), delay);
  }, [enabled, delay, flush]);

  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => {
    const warn = (e) => { if (pending.current) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, []);

  return { ...state, schedule, flush, reset, hasPending: () => !!pending.current };
}
